class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    // Upsell card add-to-cart — event delegation on drawer root
    this.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-upsell-add]');
      if (!btn) return;
      e.preventDefault();
      const variantId = btn.dataset.variantId;
      if (!variantId) return;
      this._addUpsellToCart(variantId, btn);
    });

    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this._refreshUpsellCards());
  }

  /**
   * POST to /cart/add.js for upsell variant, then re-render drawer.
   * On success dispatches cart:change so other cart integrations stay in sync.
   */
  async _addUpsellToCart(variantId, btn) {
    btn.disabled = true;
    btn.textContent = '...';

    try {
      const resp = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      });

      if (!resp.ok) throw new Error('cart/add failed');

      // Re-render the drawer using Dawn's section rendering API
      const cartResp = await fetch(`/?sections=cart-drawer`);
      if (cartResp.ok) {
        const sectionsData = await cartResp.json();
        const html = sectionsData['cart-drawer'];
        if (html) {
          const cartDrawerEl = document.getElementById('CartDrawer');
          if (cartDrawerEl) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            const newInner = doc.querySelector('#CartDrawer');
            if (newInner) {
              cartDrawerEl.innerHTML = newInner.innerHTML;
              // Re-attach overlay listener after re-render
              const overlay = cartDrawerEl.querySelector('#CartDrawer-Overlay');
              if (overlay) overlay.addEventListener('click', this.close.bind(this));
            }
          }
        }
      }

      // Update cart bubble count
      const bubbleResp = await fetch(`/?sections=cart-icon-bubble`);
      if (bubbleResp.ok) {
        const bubbleData = await bubbleResp.json();
        const bubbleHtml = bubbleData['cart-icon-bubble'];
        if (bubbleHtml) {
          const bubbleEl = document.getElementById('cart-icon-bubble');
          if (bubbleEl) {
            bubbleEl.innerHTML = new DOMParser()
              .parseFromString(bubbleHtml, 'text/html')
              .querySelector('.shopify-section')?.innerHTML || '';
          }
        }
      }

      // Notify other integrations
      document.dispatchEvent(new CustomEvent('cart:change', { bubbles: true }));
      this._refreshUpsellCards();

    } catch (err) {
      console.error('RESET upsell add-to-cart error:', err);
      btn.disabled = false;
      btn.textContent = '+ הוסף';
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');

    // Notify sticky-ATC to hide while drawer is open (spec §1.3 kill-switch)
    document.dispatchEvent(new CustomEvent('cart-drawer:open'));

    // Filter upsell cards: hide any product already in cart
    this._refreshUpsellCards();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');

    // Notify sticky-ATC it can reappear
    document.dispatchEvent(new CustomEvent('cart-drawer:close'));
  }

  /**
   * Hide upsell cards for products already in cart.
   * Reads cart handles from data-attributes on cart item links,
   * then hides matching [data-upsell-product-handle] cards.
   * If all cards are hidden, hides the entire upsell section.
   */
  _refreshUpsellCards() {
    const upsellRoot = this.querySelector('[data-upsell-root]');
    if (!upsellRoot) return;

    // Collect handles of products currently in cart
    const cartHandles = new Set();
    this.querySelectorAll('.cart-item__name').forEach((link) => {
      const href = link.getAttribute('href') || '';
      const parts = href.split('/products/');
      if (parts.length > 1) {
        const handle = parts[1].split('?')[0].split('#')[0];
        if (handle) cartHandles.add(handle);
      }
    });

    let visibleCount = 0;
    upsellRoot.querySelectorAll('[data-upsell-product-handle]').forEach((card) => {
      const handle = card.dataset.upsellProductHandle;
      const shouldHide = cartHandles.has(handle);
      card.hidden = shouldHide;
      if (!shouldHide) visibleCount++;
    });

    // Hide entire upsell section if nothing to show
    upsellRoot.hidden = visibleCount === 0;
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
